import { useMemo } from 'react';
import { Badge, Button, makeStyles } from '@fluentui/react-components';
import { Undo2 } from 'lucide-react';

import { ACCENT, NUMERIC, STROKE, SURFACE, TEXT } from '../../theme/tokens';
import { SuggestionMatchedOn } from '../../types/identity';
import { IdentityWorkbench } from '../../hooks/useIdentityWorkbench';
import { MATCHED_ON_LABELS, StagedEdit } from './identityRowModel';

// FD-510 — the review step the mockups draw as "Select → Review → Confirm →
// Apply".
//
// THREE OF THOSE FOUR ALREADY EXISTED, which is why this is a panel and not a
// wizard. Bulk approve STAGES; nothing is written until Save; the sticky save
// bar is the confirm and the button on it is the apply. What was missing is the
// middle one — a place to see what is about to be written, grouped by the
// evidence behind it, and to take one row back out.
//
// The match-type breakdown is built from `StagedEdit.matchedOn`, which FD-504
// added to the staged model for exactly this kind of use: it carries the tier
// that produced a candidate through an approve, and is deliberately ABSENT on a
// manual add or a picker Change, because those are operator choices rather than
// matches. Those land under "Chosen directly" rather than being folded into a
// tier they did not come from, and removals are counted separately because a
// removal has no destination to have matched.

const useStyles = makeStyles({
  panel: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: STROKE.base,
    backgroundColor: SURFACE.base,
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '16px',
    paddingRight: '16px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '150px minmax(0, 1fr) 56px',
    alignItems: 'center',
    columnGap: '8px',
  },
  track: {
    height: '6px',
    borderTopLeftRadius: '3px',
    borderTopRightRadius: '3px',
    borderBottomLeftRadius: '3px',
    borderBottomRightRadius: '3px',
    backgroundColor: STROKE.base,
    overflowX: 'hidden',
  },
  fill: {
    height: '6px',
    backgroundColor: ACCENT.base,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
    maxHeight: '240px',
    overflowY: 'auto',
  },
  listRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: '4px',
  },
});

/** "Chosen directly" is not a tier — it is the absence of one, named. */
const DIRECT_LABEL = 'Chosen directly';
const REMOVAL_LABEL = 'Removals';

export interface IdentityBulkReviewPanelProps {
  wb: IdentityWorkbench;
}

export function summariseStaged(staged: Map<string, StagedEdit>): {
  total: number;
  adds: number;
  removals: number;
  byEvidence: { label: string; count: number }[];
} {
  let adds = 0;
  let removals = 0;
  const counts = new Map<string, number>();
  const bump = (label: string) => counts.set(label, (counts.get(label) ?? 0) + 1);

  for (const edit of staged.values()) {
    if (edit.kind === 'remove') {
      removals += 1;
      bump(REMOVAL_LABEL);
      continue;
    }
    adds += 1;
    bump(
      edit.matchedOn
        ? (MATCHED_ON_LABELS[edit.matchedOn as SuggestionMatchedOn] ?? edit.matchedOn)
        : DIRECT_LABEL,
    );
  }

  return {
    total: adds + removals,
    adds,
    removals,
    // Largest first — the operator is scanning for what dominates the batch.
    byEvidence: [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
  };
}

export default function IdentityBulkReviewPanel({ wb }: IdentityBulkReviewPanelProps) {
  const styles = useStyles();
  const { staged, unstage } = wb;
  const summary = useMemo(() => summariseStaged(staged), [staged]);

  if (summary.total === 0) return null;

  return (
    <section className={styles.panel} aria-label="Review staged changes">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-[13px] font-semibold" style={{ color: TEXT.primary }}>
          About to write <span style={NUMERIC}>{summary.total}</span> change
          {summary.total === 1 ? '' : 's'}
        </span>
        <span className="text-[12px]" style={{ color: TEXT.secondary }}>
          <span style={NUMERIC}>{summary.adds}</span> mapping
          {summary.adds === 1 ? '' : 's'} ·{' '}
          <span style={NUMERIC}>{summary.removals}</span> removal
          {summary.removals === 1 ? '' : 's'}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        {summary.byEvidence.map((bucket) => (
          <div key={bucket.label} className={styles.barRow}>
            <span className="text-[12px]" style={{ color: TEXT.secondary }}>
              {bucket.label}
            </span>
            {/* A bar, not a percentage. FD-492's guard forbids a printed
                percentage anywhere on these surfaces, and it is also the
                honest shape: no printed number can then disagree with the
                count beside it. */}
            <span
              className={styles.track}
              role="presentation"
              aria-hidden
            >
              <span
                className={styles.fill}
                style={{
                  width: `${Math.round((bucket.count / summary.total) * 100)}%`,
                  display: 'block',
                }}
              />
            </span>
            <span className="text-right text-[12px]" style={{ ...NUMERIC, color: TEXT.primary }}>
              {bucket.count}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.list}>
        {[...staged.entries()].map(([key, edit]) => (
          <div key={key} className={styles.listRow}>
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[13px]" style={{ color: TEXT.primary }}>
                {edit.label}
              </span>
              {edit.kind === 'remove' ? (
                <Badge appearance="tint" color="warning">
                  Removal
                </Badge>
              ) : (
                <>
                  <span className="text-[12px]" style={{ color: TEXT.secondary }}>
                    →{' '}
                    {edit.destUser.displayName ??
                      edit.destUser.userPrincipalName ??
                      edit.destUser.id}
                  </span>
                  <Badge appearance="outline" color="informative">
                    {edit.matchedOn
                      ? (MATCHED_ON_LABELS[edit.matchedOn as SuggestionMatchedOn] ??
                        edit.matchedOn)
                      : DIRECT_LABEL}
                  </Badge>
                </>
              )}
            </span>
            {/* Exclude IS unstage — one staged-edit map, one way out of it. A
                separate "excluded" set would be a second source of truth for
                what is about to be written. */}
            <Button
              size="small"
              appearance="subtle"
              icon={<Undo2 size={14} strokeWidth={1.5} />}
              aria-label={`Exclude ${edit.label} from this save`}
              onClick={() => unstage(key)}
            >
              Exclude
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
